import sharedEnUS from '@griply/assets/src/translations/en-us.json';
import { Locales } from '@griply/types';
import i18n, { BackendModule } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import enUS from 'shared/assets/localization/translations/web/en-us.json';
import { getErrorReporter } from 'shared/services/error-tracking';

type TranslationModule = { default: Record<string, string> };
type LoadableLocale = Exclude<Locales, Locales.EnUS>;

// Only the active language is parsed: all twelve were a third of every page load, the Electron quick entry's included.
const translationLoaders: Record<
  LoadableLocale,
  () => Promise<[TranslationModule, TranslationModule]>
> = {
  [Locales.NlNL]: () =>
    Promise.all([
      import('@griply/assets/src/translations/nl-nl.json'),
      import('shared/assets/localization/translations/web/nl-nl.json'),
    ]),
  [Locales.EsES]: () =>
    Promise.all([
      import('@griply/assets/src/translations/es-es.json'),
      import('shared/assets/localization/translations/web/es-es.json'),
    ]),
  [Locales.DeDE]: () =>
    Promise.all([
      import('@griply/assets/src/translations/de-de.json'),
      import('shared/assets/localization/translations/web/de-de.json'),
    ]),
  [Locales.PlPL]: () =>
    Promise.all([
      import('@griply/assets/src/translations/pl-pl.json'),
      import('shared/assets/localization/translations/web/pl-pl.json'),
    ]),
  [Locales.DaDK]: () =>
    Promise.all([
      import('@griply/assets/src/translations/da-dk.json'),
      import('shared/assets/localization/translations/web/da-dk.json'),
    ]),
  [Locales.SvSE]: () =>
    Promise.all([
      import('@griply/assets/src/translations/sv-se.json'),
      import('shared/assets/localization/translations/web/sv-se.json'),
    ]),
  [Locales.NbNO]: () =>
    Promise.all([
      import('@griply/assets/src/translations/nb-no.json'),
      import('shared/assets/localization/translations/web/nb-no.json'),
    ]),
  [Locales.ItIT]: () =>
    Promise.all([
      import('@griply/assets/src/translations/it-it.json'),
      import('shared/assets/localization/translations/web/it-it.json'),
    ]),
  [Locales.FrFR]: () =>
    Promise.all([
      import('@griply/assets/src/translations/fr-fr.json'),
      import('shared/assets/localization/translations/web/fr-fr.json'),
    ]),
  [Locales.PtPT]: () =>
    Promise.all([
      import('@griply/assets/src/translations/pt-pt.json'),
      import('shared/assets/localization/translations/web/pt-pt.json'),
    ]),
  [Locales.PtBR]: () =>
    Promise.all([
      import('@griply/assets/src/translations/pt-br.json'),
      import('shared/assets/localization/translations/web/pt-br.json'),
    ]),
};

const isLoadableLocale = (language: string): language is LoadableLocale =>
  language in translationLoaders;

const translationBackend: BackendModule = {
  type: 'backend',
  init: () => undefined,
  read: async (language, _namespace, callback) => {
    // a language-only tag ('nl') or an unsupported one has nothing to load and resolves to the fallback
    if (!isLoadableLocale(language)) {
      callback(null, {});
      return;
    }

    const loaded = await translationLoaders[language]().catch(
      (error: Error) => error,
    );
    if (loaded instanceof Error) {
      getErrorReporter().captureException(loaded, {
        tags: { source: 'i18n-translation-bundle' },
        extra: { language },
      });
      callback(loaded, null);
      return;
    }

    const [shared, web] = loaded;
    callback(null, { ...shared.default, ...web.default });
  },
};

export const i18nReady = i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .use(translationBackend)
  .init({
    fallbackLng: Locales.EnUS,
    resources: {
      [Locales.EnUS]: { translation: { ...sharedEnUS, ...enUS } },
    },
    partialBundledLanguages: true,
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['navigator'],
    },
  });

// Update HTML lang attribute when language changes
const updateHtmlLang = (lng: string) => {
  document.documentElement.setAttribute('lang', lng.split('-')[0]);
};

// Set initial language
updateHtmlLang(i18n.resolvedLanguage ?? i18n.language);

// Listen for language changes
i18n.on('languageChanged', updateHtmlLang);

export default i18n;
